import { Link } from "@tanstack/react-router";
import { CheckCircle2, Diamond, ListChecks, Sparkles } from "lucide-react";
import type { ReactNode } from "react";
import type { LifecycleStage } from "../../lifecycleWorkspace";
import { formatProposalMonth } from "../../monthlyProposalModel";
import { clientDisplayNameFromSlug } from "./clientDisplayName";

export const lifecycleStageDefinitions = [
  { position: 1, stage: "configuration", slug: "proposal-configuration", label: "Month Configuration", policyKey: "configuration" },
  { position: 2, stage: "ideation", slug: "calendar", label: "Ideation", policyKey: "calendar" },
  { position: 3, stage: "briefing", slug: "briefing", label: "Briefing", policyKey: "briefing" },
  { position: 4, stage: "design", slug: "design", label: "Design", policyKey: "design" },
  { position: 5, stage: "clientReview", slug: "client-review", label: "Client Delivery", policyKey: "clientReview" },
] as const;

export type LifecycleStageDefinition = (typeof lifecycleStageDefinitions)[number];
export type LifecycleStagePolicySummary = {
  complete?: boolean;
  accessible?: boolean;
  notStarted?: boolean;
  reason?: string;
  eligibleCount?: number;
  inProgressCount?: number;
};

export type LifecycleStageSummary = {
  stages?: Partial<Record<LifecycleStageDefinition["policyKey"], LifecycleStagePolicySummary>>;
};

export function lifecycleStageLabel(stage: LifecycleStage) {
  return lifecycleStageDefinitions.find((definition) => definition.stage === stage)?.label ?? stage;
}

export function lifecycleStagePath(params: { clientSlug: string; month: string }, stage: LifecycleStage) {
  const definition = lifecycleStageDefinitions.find((candidate) => candidate.stage === stage);
  return `/client/${encodeURIComponent(params.clientSlug)}/${encodeURIComponent(params.month)}/${definition?.slug ?? "calendar"}`;
}

export function getNextIncompleteLifecycleStage(stage: LifecycleStage, summary?: LifecycleStageSummary | null) {
  const currentIndex = lifecycleStageDefinitions.findIndex((definition) => definition.stage === stage);
  if (currentIndex < 0) return undefined;
  return lifecycleStageDefinitions.slice(currentIndex + 1).find((definition) => summary?.stages?.[definition.policyKey]?.complete !== true);
}

export function LifecycleBreadcrumbs({ clientSlug, month }: { clientSlug: string; month: string }) {
  return (
    <nav className="lifecycle-breadcrumbs" aria-label="Breadcrumb">
      <ol>
        <li><Link to="/clients">Clients</Link></li>
        <li aria-hidden="true">›</li>
        <li><Link to="/client/$clientSlug" params={{ clientSlug }}>{clientDisplayNameFromSlug(clientSlug)}</Link></li>
        <li aria-hidden="true">›</li>
        <li><span aria-current="page">{formatProposalMonth(month)}</span></li>
      </ol>
    </nav>
  );
}

export function LifecycleStageHero({ stage, description, summary, headingLevel = 1 }: { stage: LifecycleStage; description?: string; summary?: ReactNode; headingLevel?: 1 | 2 }) {
  const label = lifecycleStageLabel(stage);
  const heading = headingLevel === 2
    ? <h2 className="lifecycle-stage-hero-heading">{label}</h2>
    : <h1 className="lifecycle-stage-hero-heading">{label}</h1>;
  return (
    <header className={`lifecycle-hero lifecycle-stage-hero lifecycle-stage-hero-${stage}`} data-stage-hero={stage}>
      <div className="lifecycle-stage-hero-copy">
        {heading}
        {description ? <p className="lifecycle-stage-hero-description">{description}</p> : null}
      </div>
      {summary ? <div className="lifecycle-stage-hero-summary">{summary}</div> : null}
    </header>
  );
}

function LifecycleStageCompletionLink({ next, params, summary }: { next: LifecycleStageDefinition; params: { clientSlug: string; month: string }; summary?: LifecycleStageSummary | null }) {
  switch (next.stage) {
    case "configuration":
      return <Link className="button-link secondary" to="/client/$clientSlug/$month/proposal-configuration" params={params}>Continue to {next.label}</Link>;
    case "ideation":
      return <Link className="button-link secondary" to="/client/$clientSlug/$month/calendar" params={params}>Continue to {next.label}</Link>;
    case "briefing":
      if (summary?.stages?.briefing?.notStarted) {
        const readyCount = Number(summary.stages.calendar?.eligibleCount ?? 0);
        return <Link className="button-link secondary" to="/client/$clientSlug/$month/calendar" hash="all-content" params={params}>{readyCount > 0 ? "Review ready pieces" : "Resolve blockers"}</Link>;
      }
      return <Link className="button-link secondary" to="/client/$clientSlug/$month/briefing" params={params}>Continue to {next.label}</Link>;
    case "design":
      return <Link className="button-link secondary" to="/client/$clientSlug/$month/design" params={params}>Continue to {next.label}</Link>;
    case "clientReview":
      return <Link className="button-link secondary" to="/client/$clientSlug/$month/client-review" params={params}>Continue to {next.label}</Link>;
  }
}

export function LifecycleStageCompletionNotice({ stage, summary, params }: { stage: LifecycleStage; summary?: LifecycleStageSummary | null; params: { clientSlug: string; month: string } }) {
  const policyKey = lifecycleStageDefinitions.find((definition) => definition.stage === stage)?.policyKey;
  if (!policyKey || summary?.stages?.[policyKey]?.complete !== true) return null;
  const next = getNextIncompleteLifecycleStage(stage, summary);
  const label = lifecycleStageLabel(stage);
  const completionLabel = stage === "ideation" ? "Calendar planning complete" : `${label} completed`;
  const completionMessage = stage === "ideation"
    ? "Planning checkpoint reached. Calendar remains editable for later changes."
    : "This stage is closed. Continue with the next incomplete stage when you are ready.";
  return (
    <section className={`lifecycle-completion-stamp lifecycle-completion-stamp-${stage}`} data-stage-completion={stage} role="status" aria-live="polite">
      <span className="lifecycle-completion-stamp-mark" aria-hidden="true">Complete</span>
      <div>
        <strong>{completionLabel}</strong>
        <span>{completionMessage}</span>
      </div>
      {next ? <LifecycleStageCompletionLink next={next} params={params} summary={summary} /> : <span className="lifecycle-completion-all-done">All lifecycle stages complete</span>}
    </section>
  );
}

export type PieceLifecycleStageState = {
  stage: Exclude<LifecycleStage, "configuration">;
  state: string;
  reason?: string;
};

function stageIcon(stage: PieceLifecycleStageState["stage"]) {
  if (stage === "ideation") return <Sparkles size={17} />;
  if (stage === "briefing") return <ListChecks size={17} />;
  if (stage === "design") return <Diamond size={17} />;
  return <CheckCircle2 size={17} />;
}

function stageStatusLabel(state: string) {
  if (state === "completed") return "Complete";
  if (state === "current") return "Current";
  if (state === "locked") return "Locked";
  return "Available";
}

export function PieceLifecycleStageRail({ title, stages, viewStage, onStageChange }: { title: string; stages: PieceLifecycleStageState[]; viewStage: PieceLifecycleStageState["stage"]; onStageChange: (stage: PieceLifecycleStageState["stage"]) => void }) {
  const hasCurrent = stages.some((entry) => entry.state === "current");
  const lastStage = stages[stages.length - 1]?.stage;
  return (
    <nav className="piece-stage-rail shared-piece-stage-rail" data-component="piece-lifecycle-stage-rail" data-zone="stage-rail" aria-label={`${title} lifecycle`}>
      <div className="piece-stage-track" role="tablist" aria-label={`${title} lifecycle stages`}>
        {stages.map((entry, index) => {
          const workflowCurrent = entry.state === "current" || (!hasCurrent && entry.stage === lastStage && entry.state === "completed");
          const viewing = viewStage === entry.stage;
          const stateClass = workflowCurrent ? "is-current" : entry.state === "completed" ? "is-complete" : "is-future";
          return (
            <div className={`piece-stage-step stage-${entry.stage} ${stateClass}${viewing ? " is-viewing" : ""}`} data-stage={entry.stage} data-stage-state={entry.state} key={entry.stage}>
              <button type="button" role="tab" disabled={entry.state === "locked"} aria-selected={viewing} aria-current={workflowCurrent ? "step" : undefined} title={entry.reason} onClick={() => onStageChange(entry.stage)}>
                <span className="stage-icon" aria-hidden="true">{stageIcon(entry.stage)}</span>
                <span><strong>{index + 1}. {lifecycleStageLabel(entry.stage)}</strong><small>{stageStatusLabel(entry.state)}{entry.reason ? ` · ${entry.reason}` : ""}</small></span>
              </button>
              {index < stages.length - 1 ? <span className="stage-connector" aria-hidden="true" /> : null}
            </div>
          );
        })}
      </div>
    </nav>
  );
}
